import React, { useEffect, useState } from "react";
import {
  Chip,
  Switch,
  Box,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { Avatar, Button as JoyButton } from "@mui/joy";
import { connect, useDispatch } from "react-redux";
import { BlogPost, BlogFilters } from "../../types/blog";
import CustomPagination from "../../components/CustomPagination";
import EmptyData from "../../components/EmptyData";
import AddEditBlog from "../../Modals/AddEditBlog.tsx";
import BlogService from "../../services/blogService.ts";
import { toast } from "react-toastify";
import moment from "moment";

interface BlogProps {
  user?: any;
}

const Blog: React.FC<BlogProps> = ({ user }) => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalBlogs, setTotalBlogs] = useState<number>(0);
  const [showAddEdit, setShowAddEdit] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [selectedBlog, setSelectedBlog] = useState<BlogPost | null>(null);
  const [isPermission, setIsPermission] = useState<boolean>(false);
  const [isPermissionDelete, setIsPermissionDelete] = useState<boolean>(false);

  // Filters and pagination
  const [filters, setFilters] = useState<BlogFilters>({
    category: "",
    published: "",
    search: "",
    page: 1,
    limit: 10,
  });

  const dispatch = useDispatch();

  useEffect(() => {
    setIsPermission(
      (user?.role === "staff" && user?.permission?.p_staff > 1) ||
        user?.role !== "staff"
        ? true
        : false
    );
    setIsPermissionDelete(
      (user?.role === "staff" && user?.permission?.p_staff > 2) ||
        user?.role !== "staff"
        ? true
        : false
    );
  }, [user]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await BlogService.getBlogPosts(filters);

      if (response.status) {
        setBlogs(response.data || []);
        setTotalBlogs(response.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error("Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [filters]);

  const handleFilterChange = (field: keyof BlogFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field !== "page" ? 1 : prev.page, // Reset page when changing other filters
    }));
  };

  const handlePageChange = (page: number, limit: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
      limit,
    }));
  };

  const handleTogglePublished = async (blog: BlogPost) => {
    try {
      const response = await BlogService.updateBlogStatus(
        blog._id,
        !blog.published
      );

      if (response.status) {
        toast.success("Blog status updated successfully");
        fetchBlogs(); // Refresh the list
      }
    } catch (error) {
      console.error("Error updating blog status:", error);
      toast.error("Failed to update blog status");
    }
  };

  const handleToggleFeatured = async (blog: BlogPost) => {
    try {
      const response = await BlogService.updateBlogFeatured(
        blog._id,
        !blog.featured
      );

      if (response.status) {
        toast.success("Blog featured status updated successfully");
        fetchBlogs(); // Refresh the list
      }
    } catch (error) {
      console.error("Error updating blog featured status:", error);
      toast.error("Failed to update blog featured status");
    }
  };

  const handleEdit = (blog: BlogPost) => {
    setSelectedBlog(blog);
    setShowAddEdit(true);
  };

  const handleDelete = (blog: BlogPost) => {
    setSelectedBlog(blog);
    setShowDelete(true);
  };

  const handleCloseModals = () => {
    setShowAddEdit(false);
    setShowDelete(false);
    setSelectedBlog(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBlog) return;

    try {
      const response = await BlogService.deleteBlogPost(selectedBlog._id);

      if (response.status) {
        toast.success("Blog deleted successfully");
        fetchBlogs();
        handleCloseModals();
      }
    } catch (error) {
      console.error("Error deleting blog:", error);
      toast.error("Failed to delete blog");
    }
  };

  const getStatusColor = (published: boolean) => {
    return published ? "success" : "default";
  };

  const getFeaturedColor = (featured: boolean) => {
    return featured ? "warning" : "default";
  };

  return (
    <div className="mt-1">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-500">Blog Management</h3>
          <button
            className="btn common-button"
            disabled={!isPermission}
            onClick={() => setShowAddEdit(true)}
          >
            Add New Blog
          </button>
        </div>

        {/* Filters */}
        <Box className="mb-4">
          <div className="row">
            <div className="col-md-3">
              <TextField
                fullWidth
                size="small"
                placeholder="Search blogs..."
                value={filters.search || ""}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className="col-md-3">
              <TextField
                fullWidth
                size="small"
                select
                label="Category"
                value={filters.category || ""}
                onChange={(e) => handleFilterChange("category", e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="gaming">Gaming</MenuItem>
                <MenuItem value="auction">Auction</MenuItem>
                <MenuItem value="collectibles">Collectibles</MenuItem>
                <MenuItem value="news">News</MenuItem>
              </TextField>
            </div>
            <div className="col-md-3">
              <TextField
                fullWidth
                size="small"
                select
                label="Status"
                value={filters.published || ""}
                onChange={(e) =>
                  handleFilterChange("published", e.target.value)
                }
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">Published</MenuItem>
                <MenuItem value="false">Draft</MenuItem>
              </TextField>
            </div>
          </div>
        </Box>

        {/* Total Count */}
        <div className="col-lg-5">
          <h5 className="mt-4 fw-600">Total Blog Posts ({totalBlogs})</h5>
        </div>

        {/* Blog Table */}
        <div className="table-responsive mt-4">
          <table className="table table-hover user-management-table">
            <thead className="border-gray">
              <th>Thumbnail</th>
              <th>Title</th>
              <th>Author</th>
              <th>Category</th>
              <th>Status</th>
              <th>Featured</th>
              <th>Views</th>
              <th>Engagement</th>
              <th>Created</th>
              {isPermission && <th className="text-center">Actions</th>}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isPermission ? 10 : 9} className="text-center">
                    Loading...
                  </td>
                </tr>
              ) : blogs.length > 0 ? (
                blogs.map((blog, index) => (
                  <tr key={blog._id}>
                    <td>
                      <Avatar
                        variant="soft"
                        src={blog.featured_image || "/default-blog.png"}
                        alt={`Blog Thumbnail ${index + 1}`}
                      />
                    </td>
                    <td
                      className="cursor-pointer"
                      onClick={() => handleEdit(blog)}
                    >
                      <div>
                        <div className="fw-600">{blog.title}</div>
                        {blog.excerpt && (
                          <div className="text-muted small">
                            {blog.excerpt.substring(0, 80)}...
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {blog.author?.user_name || blog.author?.name || "Unknown"}
                    </td>
                    <td>
                      {blog.category && (
                        <Chip
                          label={blog.category}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <Switch
                          checked={blog.published}
                          onChange={() => handleTogglePublished(blog)}
                          disabled={!isPermission}
                          size="small"
                        />
                        <Chip
                          label={blog.published ? "Published" : "Draft"}
                          color={getStatusColor(blog.published)}
                          size="small"
                          className="ms-2"
                        />
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <Switch
                          checked={blog.featured}
                          onChange={() => handleToggleFeatured(blog)}
                          disabled={!isPermission}
                          size="small"
                        />
                        <Chip
                          label={blog.featured ? "Featured" : "Normal"}
                          color={getFeaturedColor(blog.featured)}
                          size="small"
                          className="ms-2"
                        />
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <ViewIcon
                          fontSize="small"
                          className="me-1 text-muted"
                        />
                        {blog.views || 0}
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="small">
                          ♥ {blog.upvoteCount || 0} upvotes
                        </div>
                        <div className="small">
                          💬 {blog.commentCount || 0} comments
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="small">
                        {moment(blog.createdAt).format("MMM DD, YYYY")}
                      </div>
                    </td>
                    {isPermission && (
                      <td className="text-center">
                        <JoyButton
                          className="p-0 text-decoration-underline me-2"
                          variant="plain"
                          color="primary"
                          onClick={() => handleEdit(blog)}
                          style={{ color: "#0d6efd" }}
                        >
                          Edit
                        </JoyButton>
                        {isPermissionDelete && (
                          <JoyButton
                            className="p-0 text-decoration-underline"
                            variant="plain"
                            color="danger"
                            onClick={() => handleDelete(blog)}
                            style={{ color: "#dc3545" }}
                          >
                            Delete
                          </JoyButton>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <EmptyData />
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-3">
          {totalBlogs > 0 && (
            <CustomPagination
              total={totalBlogs}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <AddEditBlog
        show={showAddEdit}
        handleClose={handleCloseModals}
        blogData={selectedBlog}
        onSuccess={fetchBlogs}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDelete}
        onClose={handleCloseModals}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Blog Post</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedBlog?.title}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModals} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.auth.user,
  };
};

export default connect(mapStateToProps)(Blog);
